import { useEffect, useMemo, useState } from 'react';
import api, { formatINR } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, X } from '@phosphor-icons/react';
import { ADMIN } from '@/constants/testIds';
import ImageInputField from '@/components/admin/ImageInputField';
import { computePricing } from '@/lib/pricing';

const empty = {
  name: '', description: '', category_id: '', price: '', mrp: '', unit: '',
  image_url: '', image_url_2: '', is_active: true, is_featured: false, stock_status: 'in_stock', stock: 0, tags: '',
  priority: 0, max_order_qty: 5,
  cost_price: '', min_margin_pct: '', max_margin_pct: '', competitor_price: '', offer_price: '',
};

const LOW_STOCK_THRESHOLD = 5;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/products', { params: { limit: 200, active_only: false } }).then((r) => r.data),
      api.get('/categories', { params: { active_only: false } }).then((r) => r.data),
    ]).then(([p, c]) => { setProducts(p); setCategories(c); }).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description, category_id: p.category_id,
      price: p.price, mrp: p.mrp, unit: p.unit, image_url: p.image_url,
      image_url_2: (p.images && p.images[0]) || '',
      is_active: p.is_active, is_featured: p.is_featured, stock_status: p.stock_status,
      stock: typeof p.stock === 'number' ? p.stock : 0,
      tags: (p.tags || []).join(', '),
      priority: typeof p.priority === 'number' ? p.priority : 0,
      max_order_qty: typeof p.max_order_qty === 'number' && p.max_order_qty > 0 ? p.max_order_qty : 5,
      cost_price: p.cost_price ?? '',
      min_margin_pct: p.min_margin_pct ?? '',
      max_margin_pct: p.max_margin_pct ?? '',
      competitor_price: p.competitor_price ?? '',
      offer_price: p.offer_price ?? '',
    });
    setShowForm(true);
  };

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category_id || !form.price) {
      toast.error('Name, category and price are required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name, description: form.description, category_id: form.category_id,
        price: parseFloat(form.price), mrp: parseFloat(form.mrp || form.price),
        unit: form.unit, image_url: form.image_url,
        images: form.image_url_2 ? [form.image_url_2.trim()] : [],
        is_active: form.is_active, is_featured: form.is_featured,
        stock_status: form.stock_status,
        stock: Math.max(0, parseInt(form.stock, 10) || 0),
        tags: form.tags.split(',').map((s) => s.trim()).filter(Boolean),
        priority: Math.max(0, parseInt(form.priority, 10) || 0),
        max_order_qty: Math.max(1, parseInt(form.max_order_qty, 10) || 5),
        cost_price: parseFloat(form.cost_price) || 0,
        min_margin_pct: parseFloat(form.min_margin_pct) || 0,
        max_margin_pct: parseFloat(form.max_margin_pct) || 0,
        competitor_price: parseFloat(form.competitor_price) || 0,
        offer_price: parseFloat(form.offer_price) || 0,
      };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/products', payload);
        toast.success('Product created');
      }
      setShowForm(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleStock = async (p) => {
    const next = p.stock_status === 'in_stock' ? 'out_of_stock' : 'in_stock';
    // Optimistic update
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, stock_status: next } : x)));
    try {
      await api.put(`/products/${p.id}`, { stock_status: next });
      toast.success(next === 'in_stock' ? 'Marked In Stock' : 'Marked Out of Stock');
    } catch (err) {
      // Revert
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, stock_status: p.stock_status } : x)));
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm';

  const pricing = useMemo(() => computePricing({
    cost_price: form.cost_price,
    min_margin_pct: form.min_margin_pct,
    max_margin_pct: form.max_margin_pct,
    competitor_price: form.competitor_price,
    current_price: form.price,
  }), [form.cost_price, form.min_margin_pct, form.max_margin_pct, form.competitor_price, form.price]);

  const statusStyle = {
    competitive: 'bg-olive-100 text-olive-800',
    high: 'bg-amber-100 text-amber-800',
    low: 'bg-red-100 text-red-700',
    unknown: 'bg-stone-100 text-stone-500',
  };

  const applySuggested = () => setForm((f) => ({ ...f, price: String(pricing.suggested_price) }));
  const applyOffer = () => setForm((f) => ({ ...f, price: String(pricing.suggested_offer_price), offer_price: String(pricing.suggested_offer_price) }));

  const lowStockCount = products.filter((p) => (typeof p.stock === 'number' ? p.stock : 0) <= LOW_STOCK_THRESHOLD).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl text-stone-900">Products</h1>
        <button data-testid={ADMIN.addProductBtn} onClick={openCreate} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-terracotta-600 text-white text-sm font-semibold hover:bg-terracotta-700"><Plus size={16} weight="bold"/> Add Product</button>
      </div>

      {!loading && lowStockCount > 0 && (
        <div data-testid="admin-low-stock-banner" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-semibold">{lowStockCount}</span> product{lowStockCount > 1 ? 's are' : ' is'} low on stock (≤ {LOW_STOCK_THRESHOLD} units). Restock soon.
        </div>
      )}

      {loading ? <p className="text-sm text-stone-500">Loading…</p> : (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-cream-100">
              <tr className="text-left text-stone-500 text-xs uppercase tracking-wider">
                <th className="py-3 px-4">Product</th><th className="px-3">Category</th><th className="px-3">Price</th><th className="px-3">Stock</th><th className="px-3">In Stock</th><th className="px-3">Status</th><th className="px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-stone-100">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <img src={p.image_url || '/placeholder.png'} alt="" className="w-10 h-10 rounded-lg object-cover bg-stone-100"/>
                      <div><p className="font-medium text-stone-900">{p.name}</p><p className="text-xs text-stone-500">{p.unit}</p></div>
                    </div>
                  </td>
                  <td className="px-3 text-stone-600 text-xs">{p.category_slug}</td>
                  <td className="px-3 font-medium">{formatINR(p.price)}</td>
                  <td className="px-3">
                    {(() => {
                      const s = typeof p.stock === 'number' ? p.stock : 0;
                      const display = Math.max(0, s);
                      const cls = s <= 0
                        ? 'bg-red-100 text-red-700'
                        : s <= LOW_STOCK_THRESHOLD
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-olive-100 text-olive-800';
                      const label = s <= 0 ? `${display} • Out` : s <= LOW_STOCK_THRESHOLD ? `${display} • Low` : `${display}`;
                      return <span data-testid={`admin-product-stock-${p.id}`} className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
                    })()}
                  </td>
                  <td className="px-3">
                    {(() => {
                      const inStock = (p.stock_status || 'in_stock') === 'in_stock';
                      return (
                        <button
                          type="button"
                          data-testid={ADMIN.productStockToggle(p.id)}
                          onClick={() => toggleStock(p)}
                          aria-pressed={inStock}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${inStock ? 'bg-olive-600' : 'bg-stone-300'}`}
                          title={inStock ? 'In Stock — click to mark Out of Stock' : 'Out of Stock — click to mark In Stock'}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${inStock ? 'translate-x-5' : 'translate-x-1'}`}
                          />
                        </button>
                      );
                    })()}
                  </td>
                  <td className="px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${p.is_active ? 'bg-olive-100 text-olive-800' : 'bg-stone-100 text-stone-500'}`}>{p.is_active ? 'Active' : 'Hidden'}</span></td>
                  <td className="px-3 text-right">
                    <button data-testid={ADMIN.productEditBtn(p.id)} onClick={() => openEdit(p)} className="w-8 h-8 rounded-lg hover:bg-stone-100 inline-grid place-items-center text-stone-600"><PencilSimple size={16}/></button>
                    <button data-testid={ADMIN.productDeleteBtn(p.id)} onClick={() => remove(p)} className="w-8 h-8 rounded-lg hover:bg-red-50 inline-grid place-items-center text-red-600"><Trash size={16}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 flex items-end sm:items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={submit} className="bg-white rounded-3xl border border-stone-200 w-full max-w-2xl p-5 sm:p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl">{editing ? 'Edit Product' : 'New Product'}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="w-8 h-8 rounded-full hover:bg-stone-100 grid place-items-center"><X size={18}/></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-stone-600">Name *</label>
                <input data-testid={ADMIN.productName} value={form.name} onChange={set('name')} className={inputCls} required/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Category *</label>
                <select data-testid={ADMIN.productCategory} value={form.category_id} onChange={set('category_id')} className={inputCls} required>
                  <option value="">Select</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Unit (e.g. 500 g)</label>
                <input data-testid={ADMIN.productUnit} value={form.unit} onChange={set('unit')} className={inputCls}/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Price (₹) *</label>
                <input data-testid={ADMIN.productPrice} type="number" step="0.01" value={form.price} onChange={set('price')} className={inputCls} required/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">MRP (₹)</label>
                <input data-testid={ADMIN.productMrp} type="number" step="0.01" value={form.mrp} onChange={set('mrp')} className={inputCls}/>
              </div>

              {/* Smart Pricing Engine (rule-based) */}
              <div className="sm:col-span-2 rounded-2xl border border-olive-100 bg-olive-50/40 p-4" data-testid="pricing-engine">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-sm text-stone-900">Smart Pricing Engine</h3>
                  <span data-testid="pricing-status" className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusStyle[pricing.status] || statusStyle.unknown}`}>{pricing.status}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <label className="text-[11px] font-medium text-stone-600">Cost Price (₹)</label>
                    <input data-testid="pricing-cost" type="number" step="0.01" value={form.cost_price} onChange={set('cost_price')} className={inputCls} placeholder="0"/>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-stone-600">Min Margin %</label>
                    <input data-testid="pricing-min-margin" type="number" step="0.1" value={form.min_margin_pct} onChange={set('min_margin_pct')} className={inputCls} placeholder="0"/>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-stone-600">Max Margin %</label>
                    <input data-testid="pricing-max-margin" type="number" step="0.1" value={form.max_margin_pct} onChange={set('max_margin_pct')} className={inputCls} placeholder="0"/>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-stone-600">Competitor (₹)</label>
                    <input data-testid="pricing-competitor" type="number" step="0.01" value={form.competitor_price} onChange={set('competitor_price')} className={inputCls} placeholder="0"/>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="bg-white rounded-lg border border-stone-200 p-2"><p className="text-stone-500">Suggested</p><p data-testid="pricing-suggested" className="font-semibold text-stone-900">{formatINR(pricing.suggested_price)}</p></div>
                  <div className="bg-white rounded-lg border border-stone-200 p-2"><p className="text-stone-500">Offer</p><p data-testid="pricing-offer" className="font-semibold text-stone-900">{formatINR(pricing.suggested_offer_price)}</p></div>
                  <div className="bg-white rounded-lg border border-stone-200 p-2"><p className="text-stone-500">Profit</p><p data-testid="pricing-profit" className="font-semibold text-stone-900">{formatINR(pricing.profit)} · {pricing.profit_pct}%</p></div>
                  <div className="bg-white rounded-lg border border-stone-200 p-2"><p className="text-stone-500">Margin</p><p data-testid="pricing-margin" className="font-semibold text-stone-900">{pricing.margin_pct}%</p></div>
                </div>
                {pricing.notes.length > 0 && (
                  <ul data-testid="pricing-notes" className="mt-2 text-[11px] text-stone-600 list-disc pl-5 space-y-0.5">
                    {pricing.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" data-testid="apply-suggested-price" onClick={applySuggested} disabled={!pricing.suggested_price} className="h-8 px-3 rounded-full bg-olive-600 text-white text-xs font-semibold hover:bg-olive-700 disabled:bg-stone-300">Use Suggested {formatINR(pricing.suggested_price)}</button>
                  <button type="button" data-testid="apply-offer-price" onClick={applyOffer} disabled={!pricing.suggested_offer_price} className="h-8 px-3 rounded-full bg-terracotta-600 text-white text-xs font-semibold hover:bg-terracotta-700 disabled:bg-stone-300">Use Offer {formatINR(pricing.suggested_offer_price)}</button>
                  <span className="text-[11px] text-stone-500 self-center">Admin override always applies — edit Price above to override.</span>
                </div>
              </div>
              <div className="sm:col-span-2">
                <ImageInputField
                  label="Image (primary)"
                  value={form.image_url}
                  onChange={set('image_url')}
                  testIdInput={ADMIN.productImage}
                  testIdUploadBtn="admin-product-image-upload"
                  testIdUrlTab="admin-product-image-url-tab"
                  testIdUploadTab="admin-product-image-upload-tab"
                />
              </div>
              <div className="sm:col-span-2">
                <ImageInputField
                  label="Image 2 (optional)"
                  value={form.image_url_2}
                  onChange={set('image_url_2')}
                  testIdInput="admin-product-image-2"
                  testIdUploadBtn="admin-product-image-2-upload"
                  testIdUrlTab="admin-product-image-2-url-tab"
                  testIdUploadTab="admin-product-image-2-upload-tab"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-stone-600">Description</label>
                <textarea data-testid={ADMIN.productDescription} rows={2} value={form.description} onChange={set('description')} className="w-full px-3 py-2 rounded-lg bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm"/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Tags (comma-separated)</label>
                <input value={form.tags} onChange={set('tags')} className={inputCls} placeholder="fresh, organic"/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Stock</label>
                <select value={form.stock_status} onChange={set('stock_status')} className={inputCls}>
                  <option value="in_stock">In stock</option>
                  <option value="out_of_stock">Out of stock</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Stock quantity</label>
                <input data-testid="admin-product-stock-input" type="number" min="0" step="1" value={form.stock} onChange={set('stock')} className={inputCls} placeholder="0"/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Priority (0 = default, 1 = top)</label>
                <input data-testid="admin-product-priority-input" type="number" min="0" step="1" value={form.priority} onChange={set('priority')} className={inputCls} placeholder="0"/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Max order qty (per customer, default 5)</label>
                <input data-testid="admin-product-max-qty-input" type="number" min="1" step="1" value={form.max_order_qty} onChange={set('max_order_qty')} className={inputCls} placeholder="5"/>
              </div>
              <label className="flex items-center gap-2 text-sm"><input data-testid={ADMIN.productActive} type="checkbox" checked={form.is_active} onChange={set('is_active')}/> Active (visible on store)</label>
              <label className="flex items-center gap-2 text-sm"><input data-testid={ADMIN.productFeatured} type="checkbox" checked={form.is_featured} onChange={set('is_featured')}/> Featured</label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" data-testid={ADMIN.productCancelBtn} onClick={() => setShowForm(false)} className="h-10 px-5 rounded-full border border-stone-300 text-stone-700">Cancel</button>
              <button type="submit" data-testid={ADMIN.productSaveBtn} disabled={busy} className="h-10 px-6 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700 disabled:bg-stone-300">{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
